import { useRef, useState } from "react";
import { styled } from "@mui/system";
import iconDelete from "../assets/icons/icon_delete.svg";
import { TerminalAttachment } from "../gateways/cms/terminalAttachment";
import {
    useGetTerminalAttachmentsQuery,
    useCreateTerminalAttachmentMutation,
    useDeleteTerminalAttachmentMutation,
} from "../gateways/cms/terminalAttachmentsApi";

export interface Props {
    terminalId: number;
    // trueの場合、アップロード・削除を無効化し一覧とダウンロードのみ表示する（端末詳細画面用）
    readOnly?: boolean;
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024 * 1024) {
        return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleString("ja-JP", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

// style
const Description = styled("p")`
    margin: 0 0 16px;
    font-size: 13px;
    color: #8a8a8a;
`;
const DropZone = styled("div")`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    margin-bottom: 24px;
    border: 2px dashed #c9cbd3;
    border-radius: 4px;
    background-color: #fafafa;

    &.dragover {
        border-color: #3a3e5d;
        background-color: #eceef2;
    }

    span {
        flex: 1;
        font-size: 13px;
        color: #666;
    }
    button {
        height: 32px;
        padding: 0 16px;
        border-radius: 2px;
        border: 2px solid #c9cbd3;
        background-color: #fff;
        cursor: pointer;

        &:disabled {
            opacity: 0.5;
            cursor: default;
        }
    }
`;
const AttachmentTable = styled("table")`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    table-layout: fixed;

    th, td {
        padding: 8px;
        text-align: left;
        border-bottom: 1px solid #e0e0e0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    th {
        color: #8a8a8a;
        font-weight: normal;
    }
    td.operation {
        position: relative;
        text-align: right;
        white-space: nowrap;
        overflow: visible;
    }
    td.operation a.iconButton,
    td.operation button.iconButton {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        margin-left: 4px;
        border: none;
        background: none;
        cursor: pointer;
        color: #3a3e5d;
        text-decoration: none;
        border-radius: 2px;

        &:hover {
            background-color: #eceef2;
        }
    }
    td.operation button.iconButton.delete {
        color: #c0392b;

        &:hover {
            background-color: #fdecea;
        }
    }
    td.operation img {
        width: 16px;
        height: 16px;
    }
`;
const EmptyState = styled("p")`
    padding: 24px 0;
    text-align: center;
    color: #8a8a8a;
    font-size: 13px;
`;
const ConfirmDelete = styled("span")`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px;
    border-radius: 4px;
    background-color: #fdecea;
    border: 1px solid #f3b8b3;
    white-space: nowrap;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);

    .label {
        font-size: 12px;
        font-weight: bold;
        color: #c0392b;
        margin-right: 2px;
    }

    button {
        height: 26px;
        padding: 0 12px;
        border-radius: 2px;
        font-size: 12px;
        cursor: pointer;
        transition: background-color 0.1s ease;

        &.cancel {
            border: 1px solid #c9cbd3;
            background-color: #fff;
            color: #3a3e5d;

            &:hover {
                background-color: #f0f0f0;
            }
        }

        &.confirm {
            border: 1px solid #c0392b;
            color: #fff;
            background-color: #c0392b;
            font-weight: bold;

            &:hover {
                background-color: #a5301f;
            }
        }
    }
`;
const ErrorText = styled("p")`
    margin: 0 0 16px;
    font-size: 13px;
    color: #c0392b;
`;

export function TerminalAttachmentSection({ terminalId, readOnly = false }: Props) {
    const { data: attachments = [], isLoading, isError } = useGetTerminalAttachmentsQuery(terminalId);
    const [createTerminalAttachment, { isLoading: isUploading }] = useCreateTerminalAttachmentMutation();
    const [deleteTerminalAttachment] = useDeleteTerminalAttachmentMutation();

    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleSelectClick = () => {
        fileInputRef.current?.click();
    };

    const uploadFile = async (file: File) => {
        if (file.size > 10 * 1024 * 1024) {
            setUploadError("ファイルサイズは10MBまでです");
            return;
        }

        setUploadError(null);
        try {
            await createTerminalAttachment({ terminalId, file }).unwrap();
        } catch (err) {
            setUploadError("アップロードに失敗しました");
        }
    };

    const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            await uploadFile(file);
        }
        e.target.value = "";
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) {
            await uploadFile(file);
        }
    };

    const handleConfirmDelete = async (attachment: TerminalAttachment) => {
        try {
            await deleteTerminalAttachment({ terminalId, attachmentId: attachment.id }).unwrap();
        } finally {
            setPendingDeleteId(null);
        }
    };

    return (
        <section>
            <Description>
                結線図・設置写真・契約書などを端末単位で保管します。ファイル形式は問いません（1ファイル10MBまで）。
            </Description>

            {!readOnly && uploadError && <ErrorText>{uploadError}</ErrorText>}

            {!readOnly && (
                <DropZone
                    className={isDragOver ? "dragover" : ""}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <span>ファイルをドラッグ&ドロップ、または</span>
                    <button type="button" onClick={handleSelectClick} disabled={isUploading}>
                        {isUploading ? "アップロード中…" : "ファイルを選択"}
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        onChange={handleFileSelected}
                    />
                </DropZone>
            )}

            {isLoading ? (
                <EmptyState>読み込み中…</EmptyState>
            ) : isError ? (
                <EmptyState>添付資料の取得に失敗しました</EmptyState>
            ) : attachments.length === 0 ? (
                <EmptyState>添付資料はまだありません</EmptyState>
            ) : (
                <AttachmentTable>
                    <colgroup>
                        <col style={{ width: "36%" }} />
                        <col style={{ width: "8%" }} />
                        <col style={{ width: "20%" }} />
                        <col style={{ width: "22%" }} />
                        <col style={{ width: readOnly ? "8%" : "14%" }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>ファイル名</th>
                            <th>サイズ</th>
                            <th>登録者</th>
                            <th>登録日時</th>
                            <th>操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {attachments.map((attachment) => (
                            <tr key={attachment.id}>
                                <td className="fileName" title={attachment.fileName}>{attachment.fileName}</td>
                                <td>{formatFileSize(attachment.fileSize)}</td>
                                <td>{attachment.uploadedBy}</td>
                                <td>{formatDateTime(attachment.createdAt)}</td>
                                <td className="operation">
                                    {!readOnly && pendingDeleteId === attachment.id ? (
                                        <ConfirmDelete>
                                            <span className="label">本当に削除しますか？</span>
                                            <button type="button" className="cancel" onClick={() => setPendingDeleteId(null)}>キャンセル</button>
                                            <button type="button" className="confirm" onClick={() => handleConfirmDelete(attachment)}>削除する</button>
                                        </ConfirmDelete>
                                    ) : (
                                        <>
                                            <a
                                                className="iconButton"
                                                href={attachment.downloadUrl}
                                                download={attachment.fileName}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label="ダウンロード"
                                            >
                                                DL
                                            </a>
                                            {!readOnly && (
                                                <button type="button" className="iconButton delete" onClick={() => setPendingDeleteId(attachment.id)} aria-label="削除">
                                                    <img src={iconDelete} alt="削除" />
                                                </button>
                                            )}
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </AttachmentTable>
            )}
        </section>
    );
}
